import React from "react";
import { Search } from "lucide-react";
import { useT, Namespace } from "@racoon/i18n";
import { Button, FilterTab, Input } from "@racoon/ui";
import { IcPlus } from "@racoon/icons";
import { LOCATION_TABS, type LocationTab } from "../../model/useDepotUrl";

interface DepotStatusFilterProps {
  activeTab: LocationTab;
  onTabChange: (tab: LocationTab) => void;
  counts: Record<LocationTab, number>;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onCreateClick?: () => void;
  onArchiveClick?: () => void;
  onRestoreClick?: () => void;
}

export const DepotStatusFilter: React.FC<DepotStatusFilterProps> = ({
  activeTab,
  onTabChange,
  counts,
  searchValue,
  onSearchChange,
  onCreateClick,
  onArchiveClick,
  onRestoreClick,
}) => {
  const t = useT();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex w-full max-w-[638px] items-start gap-2">
          {LOCATION_TABS.map((tab) => (
            <FilterTab
              key={tab}
              state={{
                key: t(Namespace.Settings, `depot.tabs.${tab}`),
                value: tab,
                count: counts[tab],
              }}
              active={tab === activeTab}
              onClick={() => onTabChange(tab)}
            />
          ))}
          <div className="relative min-w-0 flex-1">
            <Input
              type="search"
              placeholder={t(Namespace.Common, "search")}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="!h-8 !rounded-lg pl-8 text-sm"
              size="sm"
            />
            <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-text-subtle" />
          </div>
        </div>
        <div className="flex gap-2">
          {onArchiveClick && (
            <Button
              id="depot-archive-button"
              size="sm"
              variant="opacityRedWithOutline"
              onClick={onArchiveClick}
            >
              {t(Namespace.Buttons, "archive")}
            </Button>
          )}
          {onRestoreClick && (
            <Button
              id="depot-restore-button"
              size="sm"
              variant="opacityBlueWithOutline"
              onClick={onRestoreClick}
            >
              {t(Namespace.Buttons, "restore")}
            </Button>
          )}
          {onCreateClick && (
            <Button
              id="depot-add-button"
              size="sm"
              className="gap-1 !px-3 !bg-blue-600 !text-sm font-medium tracking-[-0.2px] hover:!bg-blue-700"
              onClick={onCreateClick}
            >
              <IcPlus className="-mx-0.5 size-5" />
              {t(Namespace.Settings, "depot.addLocation")}
            </Button>
          )}
        </div>
      </div>
      {activeTab === "global" && (
        <p className="text-sm leading-4 tracking-[-0.01em] text-text-subtle">
          {t(Namespace.Settings, "depot.globalHint")}
        </p>
      )}
    </div>
  );
};
